import { SimpleTable } from '@core/components/Table';
import { TableActionButton, TableActionButtonDelete, TableActionButtonEdit } from '@core/components/TableAction';
import TableActionContainer from '@core/components/TableAction/TableActionContainer';
import { useReduxLoading, useReduxMessage } from '@core/hooks';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Competencia, DataBR } from '@format/DATA';
import CancelarBaixaIcon from 'app/assets/icon/cancelarBaixaIcon';
import { fechamentoPeriodoApi } from 'app/services/api/fechamentoPeriodoApi';
import { getCurrency, getCurrencySemSinalMonetario } from 'app/utils/monetarioUtil';
import { trigger } from 'devextreme/events';
import moment from 'moment';
import React from 'react';
import { FaEye, FaHandHoldingUsd } from 'react-icons/fa';
import ContasPagarBotao from '../components/ContasPagarBotao';
import { ContasPagarAction } from '../store';
import { ContasPagarUtil } from '../store/contasPagarAction';
import { useContasPagar } from '../store/contasPagarContext';
import { contasPagarStyles } from './styles';

const ContasPagarTable = ({ isConciliacao = false, extratoSelecionado, onTour, onFetchData }) => {
  const [state, dispatch] = useContasPagar();

  const {
    message,
    page,
    selecionados,
    carregandoRegistro,
    carregandoRegistros,
    somatorios,
    tipoSelecao,
    filtroAvancadoFormatado,
    filtroAvancado,
    requestParams,
  } = state;

  useReduxLoading(carregandoRegistro);

  useReduxMessage(message);

  const styles = contasPagarStyles();

  const handleAcaoDespesaOrigem = (action, contaPagar) => {
    if (contaPagar.parcelamentoDespesaOrigemId) {
      action(dispatch, [contaPagar.parcelamentoDespesaOrigemId], {
        numeroParcelas: contaPagar?.numeroParcelas,
        parcela: contaPagar?.parcela,
        contaPagarParcela: contaPagar,
      });
    } else if (contaPagar.retencaoDespesaOrigemId) {
      const ids = [contaPagar.retencaoDespesaOrigemId];

      if (action === ContasPagarAction.requestVisualizar) {
        ids.push(contaPagar.id);
      }

      action(dispatch, ids, {
        retencaoId: contaPagar.id,
      });
    } else {
      action(dispatch, [contaPagar.id], {});
    }
  };

  const handleEditar = async (contaPagar) => {
    const response = await fechamentoPeriodoApi.buscarUltimoPeriodo('COMPRAS_PAGAMENTO', contaPagar.dataVencimento);
    if (response.data) {
      if (response.data.bloquearAnteriores) {
        if (
          moment(contaPagar.dataVencimento).isSameOrBefore(response.data.periodo) &&
          response.data.funcionalidades.includes('COMPRAS_PAGAMENTO')
        ) {
          throw dispatch(
            ContasPagarAction.erro(
              'Não é possível lançar, editar e excluir contas com data de vencimento referente a um período já fechado no sistema.'
            )
          );
        }
      }
    }

    if (contaPagar.retencaoDespesaOrigemId) {
      // RNE-113: Quando a despesa selecionada for uma retenção,
      // o sistema deve exibir a aba "Retenções" aberta por padrão.
      ContasPagarAction.selecionarAbaCadastro(dispatch, 1);
    }
    handleAcaoDespesaOrigem(ContasPagarAction.requestEditar, contaPagar);
  };

  const handleVisualizar = (contaPagar) => {
    handleAcaoDespesaOrigem(ContasPagarAction.requestVisualizar, contaPagar);
  };

  const handleExcluir = async (contaPagar) => {
    handleAcaoDespesaOrigem(ContasPagarAction.excluir, contaPagar);
  };

  const handleCancelarBaixa = (contaPagar) => {
    ContasPagarAction.cancelarBaixas(dispatch, [contaPagar], requestParams, filtroAvancado);
  };

  const getSituacaoTag = (situacao, baixas) => {
    if (situacao === 'ABERTA') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeAberta}`}>Aberta</div>
        </div>
      );
    }
    if (situacao === 'BAIXADA') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeBaixada}`}>Baixada</div>
        </div>
      );
    }
    if (situacao === 'EXCLUIDA') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeExcluida}`}>Excluída</div>
        </div>
      );
    }
    if (situacao === 'PARCIAL') {
      return (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgePagoParcialmente}`}>Parcial</div>
        </div>
      );
    }
    if (situacao === 'PARCIALMENTE_BAIXADA') {
      return baixas?.some((baixa) => baixa?.id) ? (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeBaixada}`}>Baixada</div>
        </div>
      ) : (
        <div className={styles.situacaoContainer}>
          <div className={`${styles.badge} ${styles.badgeAberta}`}>Aberta</div>
        </div>
      );
    }
    return '-';
  };

  const actionsButtons = (contaPagar) => {
    const despesaBaixada = contaPagar.baixas ? !!contaPagar?.baixas[0]?.id : false;
    const despesaExcluida = contaPagar?.situacao === 'EXCLUIDA';

    if (isConciliacao) {
      return (
        <TableActionContainer style={{ display: 'block' }} onTour={onTour}>
          <TableActionButton
            title="Visualizar"
            icon={<FaEye size={10} />}
            onClick={() => handleVisualizar(contaPagar)}
          />
        </TableActionContainer>
      );
    } else {
      return (
        <TableActionContainer onTour={onTour}>
          {(permissaoUtils.contaPagar().editar || onTour) && contaPagar.situacao !== 'EXCLUIDA' && (
            <>
              <TableActionButtonEdit
                onClick={() => {
                  handleEditar(contaPagar);
                }}
                className={'stepIconeEditar'}
              />
            </>
          )}
          {((!despesaBaixada && contaPagar.situacao !== 'EXCLUIDA' && permissaoUtils.contaPagar().excluir) ||
            onTour) && (
            <>
              <TableActionButtonDelete onClick={() => handleExcluir(contaPagar)} className={'stepIconeExcluir'} />
            </>
          )}

          {(((!despesaBaixada || contaPagar?.situacao === 'PARCIAL') &&
            !despesaExcluida &&
            permissaoUtils.contaPagar().editar) ||
            onTour) && (
            <>
              <TableActionButton
                title="Baixar"
                icon={<FaHandHoldingUsd size={10} className={'stepIconeBaixar'} />}
                onClick={() => {
                  ContasPagarAction.requestBaixa(dispatch, [contaPagar]);
                }}
              />
            </>
          )}

          {((despesaBaixada && permissaoUtils.contaPagar().editar) || onTour) && (
            <>
              <TableActionButton
                size={10}
                title="Cancelar baixa"
                icon={<CancelarBaixaIcon size={10} className={'stepIconeCancelarBaixa'} />}
                onClick={() => handleCancelarBaixa(contaPagar)}
              />
            </>
          )}
        </TableActionContainer>
      );
    }
  };

  const getValorTotal = (data) => {
    let total = 0;
    if (data.categorias?.length !== 0) {
      for (let i = 0; i < data.categorias.length; i++) {
        total += data.categorias[i].valorBruto;
      }
      return total;
    }
    return data.valorLiquido;
  };

  // const getValorPago = (data) => {
  //   if (data?.baixas) {
  //     let total = 0;
  //     for (let i = 0; i < data.baixas.length; i++) {
  //       total += data.baixas[i].valorPago;
  //     }
  //     data.valorPago = total;
  //     return total;
  //   } else {
  //     return 0;
  //   }
  // };

  const getDataPagamento = (data) => {
    if (data?.baixas) {
      return data.baixas.reduce((latest, baixa) => {
        return !latest || moment(baixa.dataPagamento).isAfter(latest) ? baixa.dataPagamento : latest;
      }, null);
    } else {
      return undefined;
    }
  };

  const getDataLiquidacao = (data) => {
    if (data?.baixas) {
      return data.baixas.reduce((latest, baixa) => {
        return !latest || moment(baixa.dataLiquidacao).isAfter(latest) ? baixa.dataLiquidacao : latest;
      }, null);
    } else {
      return undefined;
    }
  };

  const celulaPagamento = (data) => {
    const dataPagamento = DataBR(getDataPagamento(data));
    const dataLiquidacao = getDataLiquidacao(data) ? 'Liquidado em ' + DataBR(getDataLiquidacao(data)) : null;

    if (!dataLiquidacao) {
      return <span>{dataPagamento}</span>;
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          lineHeight: 1.1,
        }}
      >
        <span>{dataPagamento}</span>
        <span style={{ fontSize: '9px' }}>{dataLiquidacao}</span>
      </div>
    );
  };

  const columns = [
    {
      name: 'categorias.classe.indice,parcela',
      title: 'Categoria',
      getCellValue: (data) =>
        data ? (
          <>
            {ContasPagarUtil.categoriaFormat(data)}
            {ContasPagarUtil.parcelaFormat(data)}
          </>
        ) : (
          '-'
        ),

      getTooltip: (data) =>
        data ? (
          <>
            {ContasPagarUtil.categoriaFormat(data)}
            {ContasPagarUtil.parcelaFormat(data)}
            <div>{ContasPagarUtil.documentoFiscalFormat(data)}</div>
          </>
        ) : (
          '-'
        ),

      sortingEnabled: true,
    },
    {
      name: 'fornecedor.pessoa.nome',
      title: 'Fornecedor',
      getCellValue: (data) => data.fornecedor?.pessoa.nome,
      getTooltip: (data) =>
        data.favorecido?.pessoa?.nome ? (
          <div>
            {data.fornecedor?.pessoa.nome} <br /> Favorecido: {data.favorecido.pessoa.nome}
          </div>
        ) : (
          data.fornecedor?.pessoa.nome
        ),
      sortingEnabled: true,
    },
    {
      name: 'competencia',
      title: 'Competência',
      align: 'center',
      hideTooltip: true,
      getCellValue: (data) => Competencia(data.competencia),
      sortingEnabled: true,
    },
    /* RNE-088: O sistema deve exibir a coluna "Nosso número" somente quando a empresa utilizar Borderô de pagamentos */
    // {
    //   name: 'numeroDocumento',
    //   title: 'Nosso número',
    // },
    {
      name: 'dataVencimento',
      title: <div className="stepIconeOrdenacao">Vencimento</div>,
      hideTooltip: true,
      align: 'center',
      getCellValue: (data) => DataBR(data.dataVencimento),
      sortingEnabled: true,
    },
    {
      name: 'dataPagamento',
      title: 'Pagamento',
      hideTooltip: true,
      align: 'center',
      getCellValue: (data) => celulaPagamento(data),
      sortingEnabled: true,
    },
    {
      name: 'valorLiquido',
      title: 'Valor líquido (R$)',
      align: 'right',
      hideTooltipFunction: (data) => data.numeroParcelas === 0,
      getCellValue: (data) => (data.valorLiquido ? getCurrencySemSinalMonetario(data.valorLiquido) : '-'),
      getTooltip: (data) =>
        data ? (
          <>
            Valor total: <div>{getCurrencySemSinalMonetario(getValorTotal(data))}</div>
          </>
        ) : (
          '-'
        ),
      sortingEnabled: true,
    },
    {
      name: 'valorTotalPago',
      title: 'Valor Pago (R$)',
      hideTooltip: true,
      align: 'right',
      getCellValue: (data) => {
        return data?.valorPago !== null && data?.valorPago !== undefined ? getCurrency(data?.valorPago) : undefined;
      },
      sortingEnabled: true,
    },
    {
      name: 'situacao',
      title: 'Situação',
      hideTooltip: trigger,
      getCellValue: (data) => getSituacaoTag(data.situacao, data.baixas) || '-',
      width: 100,
      sortingEnabled: true,
    },
    {
      name: '',
      title: '',
      sortingEnabled: false,
      align: 'left',
      width: 110,
      getCellValue: (contaPagar) => actionsButtons(contaPagar),
    },
  ];

  // const onFetchData = (params) => {
  //   ContasPagarAction.carregarRegistros(dispatch, params, filtroAvancadoFormatado, onTour);
  // };

  const onSelectionChange = (params) => {
    dispatch(ContasPagarAction.setSelecionados(params));
  };

  const onCurrentPageChange = (params) => {
    dispatch(ContasPagarAction.setCurrentPage(params));
  };

  const onPageSizeChange = (params) => dispatch(ContasPagarAction.setPageSize(params));
  const onSortingChange = (params) => dispatch(ContasPagarAction.setSorting(params));

  const getTotalDespesasSelecionadas = () => {
    let total = 0;
    if (tipoSelecao === 'TODOS_FILTRO') {
      total = somatorios.somatorioTodasContas;
    } else if (selecionados) {
      selecionados.forEach((despesa) => {
        if (isConciliacao) {
          if (despesa.situacao === 'BAIXADA') {
            despesa.baixas.forEach((baixa) => {
              total += baixa.valorPago;
            });
          } else {
            total += despesa.valorLiquido;
          }
        } else {
          total += despesa.valorLiquido;
        }
      });
    }

    return total;
  };
  const getValorDiferenca = () => {
    const somatorioSelecionados = getTotalDespesasSelecionadas() || 0;
    return Math.abs(extratoSelecionado?.valor) - somatorioSelecionados;
  };

  const footerTable = (
    <div>
      <div style={{ padding: '12px 0 0 24px' }}>
        Valor total das despesas (R$):
        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
          .format(somatorios?.somatorioFiltro ? somatorios.somatorioFiltro : 0)
          .replace('R$', '')}
      </div>
      <div style={{ padding: '12px 0 0 24px' }}>
        Valor das despesas selecionadas (R$):
        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
          .format(getTotalDespesasSelecionadas())
          .replace('R$', '')}
      </div>
    </div>
  );

  const footerConciliacao = (
    <div>
      <div style={{ padding: '12px 0 0 24px', fontWeight: 'bold' }}>
        Valor total do extrato (R$):
        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
          .format(Math.abs(extratoSelecionado?.valor))
          .replace('R$', '')}
      </div>
      <div style={{ padding: '12px 0 0 24px', fontWeight: 'bold' }}>
        Valor das despesas selecionadas (R$):
        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
          .format(getTotalDespesasSelecionadas())
          .replace('R$', '')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'end' }}>
        <div style={{ padding: '12px 0 0 24px', fontWeight: 'bold' }}>Valor da diferença (R$):</div>
        <div style={{ color: 'red', fontWeight: 'bold', marginLeft: 2 }}>
          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
            .format(getValorDiferenca())
            .replace('R$', '')}
        </div>
      </div>
    </div>
  );

  const hiddenColumnNames = [];

  if (!isConciliacao) {
    if (
      filtroAvancado.situacao !== 'BAIXADA' &&
      filtroAvancado.situacao !== 'PARCIAL' &&
      filtroAvancado.situacao !== 'TODAS'
    ) {
      hiddenColumnNames.push('dataPagamento');
      hiddenColumnNames.push('valorTotalPago');
    }
  } else {
    if (
      filtroAvancado.situacao !== 'BAIXADA' &&
      filtroAvancado.situacao !== 'ABERTA_BAIXADA' &&
      filtroAvancado.situacao !== 'PARCIAL'
    ) {
      hiddenColumnNames.push('valorTotalPago');
    }
    hiddenColumnNames.push('situacao');
    hiddenColumnNames.push('dataPagamento');
  }

  return (
    <>
      <SimpleTable
        defaultSorting={[{ columnName: 'dataVencimento', direction: 'desc' }]}
        columns={columns}
        rows={page?.content}
        selection={selecionados}
        loading={carregandoRegistros}
        totalCount={state.page?.totalElements}
        searchBy={state.requestParams?.q}
        fetchData={onFetchData}
        onRowClick={handleVisualizar}
        onSelectionChange={onSelectionChange}
        onCurrentPageChange={onCurrentPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        selectionType={state.tipoSelecao}
        onSelectionTypeChange={(params) => {
          dispatch(ContasPagarAction.setTipoSelecao(params));
        }}
        params={filtroAvancadoFormatado}
        hiddenColumnNames={hiddenColumnNames}
        footerTable={isConciliacao ? footerConciliacao : footerTable}
      />
      {!isConciliacao &&
        (permissaoUtils.contaPagar().cadastrar ||
          permissaoUtils.contaPagar().excluir ||
          permissaoUtils.contaPagar().editar) && <ContasPagarBotao />}
    </>
  );
};

export default ContasPagarTable;
